/**
 * Compatibilità AppHero Free Gift + cart drawer dinamico Shopify.
 *
 * Problema:
 * AppHero può caricare la sezione generica "cart-drawer" invece
 * dell'istanza dinamica configurata nel tema.
 *
 * Soluzione:
 * 1. Memorizza l'ID dinamico e l'header originale del drawer.
 * 2. Quando compare il drawer generico, richiede a Shopify la sezione corretta.
 * 3. Sostituisce soltanto .drawer__content.
 * 4. Conserva il pulsante Chiudi originale e i suoi eventi.
 * 5. Aggiunge una chiusura di sicurezza per X, overlay ed Escape.
 */

(() => {
  'use strict';

  const selectors = {
    dynamicCartItems:
      'cart-items[data-section-id^="sections--"][data-section-id$="__cart-drawer"]',

    dynamicMiniCart:
      '[id^="MiniCart-sections--"][id$="__cart-drawer"]',

    dynamicSectionWrapper:
      '[id^="shopify-section-sections--"][id$="__cart-drawer"]',

    genericMiniCart: '#MiniCart-cart-drawer',
    cartDrawer: '#CartDrawer',
    drawerInner: '.drawer__inner',
    drawerHeader: '.drawer__header',
    drawerContent: '.drawer__content',
    closeButton: '.drawer__close',
    cartCount: 'cart-count'
  };

  let correctSectionId = null;
  let originalDrawerHeader = null;
  let restorationRunning = false;
  let restorationTimer = null;

  /**
   * Trova l'ID dinamico assegnato da Shopify al cart drawer.
   */
  function findCorrectSectionId() {
    const cartItems = document.querySelector(
      selectors.dynamicCartItems
    );

    if (cartItems?.dataset?.sectionId) {
      return cartItems.dataset.sectionId;
    }

    const miniCart = document.querySelector(
      selectors.dynamicMiniCart
    );

    if (miniCart?.id) {
      return miniCart.id.replace(/^MiniCart-/, '');
    }

    const sectionWrapper = document.querySelector(
      selectors.dynamicSectionWrapper
    );

    if (sectionWrapper?.id) {
      return sectionWrapper.id.replace(
        /^shopify-section-/,
        ''
      );
    }

    return null;
  }

  /**
   * Memorizza l'ID dinamico e il vero header del tema prima che
   * AppHero possa sostituire il contenuto del drawer.
   */
  function captureOriginalDrawerState() {
    const discoveredSectionId = findCorrectSectionId();

    if (discoveredSectionId) {
      correctSectionId = discoveredSectionId;
    }

    if (originalDrawerHeader || !discoveredSectionId) {
      return;
    }

    const dynamicMiniCart = document.querySelector(
      selectors.dynamicMiniCart
    );

    const drawer =
      dynamicMiniCart?.closest(selectors.cartDrawer) ||
      document.querySelector(selectors.cartDrawer);

    const header = drawer?.querySelector(
      selectors.drawerHeader
    );

    if (header) {
      /*
       * Salviamo il vero nodo e non un clone.
       * In questo modo restano collegati gli eventi del tema.
       */
      originalDrawerHeader = header;
    }
  }

  /**
   * Richiede a Shopify l'HTML aggiornato della sezione corretta.
   */
  async function fetchCorrectDrawer(sectionId) {
    const shopRoot =
      window.Shopify?.routes?.root || '/';

    const requestUrl = new URL(
      shopRoot,
      window.location.origin
    );

    requestUrl.searchParams.set(
      'sections',
      sectionId
    );

    requestUrl.searchParams.set(
      '_apphero_cart_fix',
      Date.now().toString()
    );

    const response = await fetch(
      requestUrl.toString(),
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Errore Shopify Section Rendering API: ${response.status}`
      );
    }

    const sections = await response.json();
    const sectionHtml = sections?.[sectionId];

    if (!sectionHtml) {
      throw new Error(
        `Shopify non ha restituito la sezione "${sectionId}".`
      );
    }

    const parsedDocument =
      new DOMParser().parseFromString(
        sectionHtml,
        'text/html'
      );

    const freshDrawer =
      parsedDocument.querySelector(
        selectors.cartDrawer
      );

    if (!freshDrawer) {
      throw new Error(
        'Il drawer corretto non è presente nella risposta Shopify.'
      );
    }

    return freshDrawer;
  }

  /**
   * Ripristina il vero header del tema conservando gli eventi
   * associati al pulsante di chiusura.
   */
  function restoreOriginalHeader(drawer) {
    if (!drawer || !originalDrawerHeader) {
      return;
    }

    const currentHeader = drawer.querySelector(
      selectors.drawerHeader
    );

    if (currentHeader === originalDrawerHeader) {
      return;
    }

    if (currentHeader) {
      currentHeader.replaceWith(
        originalDrawerHeader
      );
    } else {
      const drawerInner = drawer.querySelector(
        selectors.drawerInner
      );

      drawerInner?.prepend(
        originalDrawerHeader
      );
    }
  }

  /**
   * Aggiorna il contatore dell'header senza sostituire l'header.
   */
  function updateCartCount(
    currentDrawer,
    freshDrawer
  ) {
    const currentCount =
      currentDrawer?.querySelector(
        selectors.cartCount
      );

    const freshCount =
      freshDrawer?.querySelector(
        selectors.cartCount
      );

    if (!currentCount || !freshCount) {
      return;
    }

    currentCount.textContent =
      freshCount.textContent;

    const freshLabel =
      freshCount.getAttribute('aria-label');

    if (freshLabel) {
      currentCount.setAttribute(
        'aria-label',
        freshLabel
      );
    } else {
      currentCount.removeAttribute(
        'aria-label'
      );
    }
  }

  /**
   * Controlla se il drawer risulta ancora aperto.
   */
  function isDrawerOpen(drawer) {
    if (!drawer) {
      return false;
    }

    const expandedControl =
      document.querySelector(
        '[aria-controls="CartDrawer"][aria-expanded="true"]'
      );

    return Boolean(
      expandedControl ||
        drawer.hasAttribute('open') ||
        drawer.hasAttribute('active') ||
        drawer.getAttribute('aria-hidden') ===
          'false' ||
        drawer.classList.contains('active') ||
        drawer.classList.contains(
          'is-active'
        ) ||
        drawer.classList.contains('open') ||
        drawer.classList.contains('is-open')
    );
  }

  /**
   * Chiusura manuale di sicurezza.
   * Viene utilizzata solo se il tema non riesce a chiudere il drawer.
   */
  function forceCloseDrawer(drawer) {
    if (!drawer) {
      return;
    }

    drawer.removeAttribute('open');
    drawer.removeAttribute('active');

    drawer.setAttribute(
      'aria-hidden',
      'true'
    );

    drawer.setAttribute(
      'aria-expanded',
      'false'
    );

    drawer.classList.remove(
      'active',
      'is-active',
      'open',
      'is-open'
    );

    document
      .querySelectorAll(
        '[aria-controls="CartDrawer"]'
      )
      .forEach((control) => {
        control.setAttribute(
          'aria-expanded',
          'false'
        );
      });

    drawer
      .querySelectorAll(
        [
          'overlay-element',
          '.drawer__overlay',
          '.fixed-overlay',
          '.overlay'
        ].join(', ')
      )
      .forEach((overlay) => {
        overlay.setAttribute(
          'aria-expanded',
          'false'
        );

        overlay.classList.remove(
          'active',
          'is-active',
          'open',
          'is-open'
        );
      });

    document.documentElement.classList.remove(
      'overflow-hidden',
      'drawer-open',
      'is-drawer-open',
      'menu-open'
    );

    document.body.classList.remove(
      'overflow-hidden',
      'drawer-open',
      'is-drawer-open',
      'menu-open'
    );
  }

  /**
   * Prova prima i metodi messi a disposizione dal tema.
   * Se il drawer resta aperto, applica la chiusura manuale.
   */
  function closeCartDrawerFallback() {
    const drawer = document.querySelector(
      selectors.cartDrawer
    );

    if (!drawer) {
      return;
    }

    const supportedMethods = [
      'close',
      'hide',
      'dismiss'
    ];

    for (const methodName of supportedMethods) {
      if (
        typeof drawer[methodName] ===
        'function'
      ) {
        try {
          drawer[methodName]();
          break;
        } catch (error) {
          console.warn(
            `[AppHero Cart Fix] Metodo ${methodName} non riuscito:`,
            error
          );
        }
      }
    }

    /*
     * Prova anche il tasto Escape, nel caso il tema
     * utilizzi questo evento per chiudere il drawer.
     */
    document.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        keyCode: 27,
        which: 27,
        bubbles: true,
        cancelable: true
      })
    );

    window.setTimeout(() => {
      if (isDrawerOpen(drawer)) {
        forceCloseDrawer(drawer);
      }
    }, 100);
  }

  /**
   * Sostituisce soltanto il contenuto del drawer generico
   * con quello dell'istanza dinamica configurata nel tema.
   */
  async function restoreCorrectDrawer() {
    if (restorationRunning) {
      return;
    }

    captureOriginalDrawerState();

    const genericDrawerPresent =
      document.querySelector(
        selectors.genericMiniCart
      );

    if (!genericDrawerPresent) {
      return;
    }

    if (!correctSectionId) {
      console.warn(
        '[AppHero Cart Fix] Impossibile individuare il section ID dinamico.'
      );

      return;
    }

    const currentDrawer =
      document.querySelector(
        selectors.cartDrawer
      );

    if (!currentDrawer) {
      return;
    }

    /*
     * Ripristina subito l'header originale.
     * Il pulsante Chiudi può così funzionare anche mentre
     * aspettiamo la risposta di Shopify.
     */
    restoreOriginalHeader(currentDrawer);

    restorationRunning = true;

    try {
      const freshDrawer =
        await fetchCorrectDrawer(
          correctSectionId
        );

      /*
       * AppHero potrebbe modificare nuovamente il DOM
       * mentre la richiesta è in corso.
       * Recuperiamo quindi gli elementi attualmente presenti.
       */
      const liveDrawer =
        document.querySelector(
          selectors.cartDrawer
        );

      const liveDrawerContent =
        liveDrawer?.querySelector(
          selectors.drawerContent
        );

      const freshDrawerContent =
        freshDrawer.querySelector(
          selectors.drawerContent
        );

      if (!liveDrawer || !liveDrawerContent) {
        throw new Error(
          'Il drawer attuale non è più presente nella pagina.'
        );
      }

      if (!freshDrawerContent) {
        throw new Error(
          'Il contenuto .drawer__content non è presente nella sezione.'
        );
      }

      /*
       * Mantiene header e pulsante Chiudi originali.
       * Sostituisce solamente prodotti, footer e contenuto.
       */
      restoreOriginalHeader(liveDrawer);

      liveDrawerContent.replaceWith(
        freshDrawerContent
      );

      updateCartCount(
        liveDrawer,
        freshDrawer
      );

      const closeButton =
        liveDrawer.querySelector(
          selectors.closeButton
        );

      if (
        closeButton &&
        isDrawerOpen(liveDrawer)
      ) {
        closeButton.setAttribute(
          'aria-expanded',
          'true'
        );
      }

      document.dispatchEvent(
        new CustomEvent(
          'apphero:cart-drawer-restored',
          {
            detail: {
              sectionId: correctSectionId
            }
          }
        )
      );

      console.info(
        '[AppHero Cart Fix] Drawer corretto ripristinato:',
        correctSectionId
      );
    } catch (error) {
      console.error(
        '[AppHero Cart Fix]',
        error
      );
    } finally {
      restorationRunning = false;
    }
  }

  /**
   * Evita richieste ripetute mentre AppHero effettua
   * più modifiche consecutive al DOM.
   */
  function scheduleRestoration() {
    captureOriginalDrawerState();

    window.clearTimeout(
      restorationTimer
    );

    if (
      !document.querySelector(
        selectors.genericMiniCart
      )
    ) {
      return;
    }

    restorationTimer =
      window.setTimeout(() => {
        restoreCorrectDrawer();
      }, 120);
  }

  /**
   * Intercetta il click sulla X o sull'overlay senza bloccare
   * il normale comportamento del tema.
   */
  function bindCloseFallbacks() {
    document.addEventListener(
      'click',
      (event) => {
        const target =
          event.target instanceof Element
            ? event.target
            : null;

        if (!target) {
          return;
        }

        const closeButton =
          target.closest(
            '#CartDrawer .drawer__close'
          );

        const overlayControl =
          target.closest(
            [
              'overlay-element[aria-controls="CartDrawer"]',
              '.drawer__overlay[aria-controls="CartDrawer"]',
              '.fixed-overlay[aria-controls="CartDrawer"]'
            ].join(', ')
          );

        if (
          !closeButton &&
          !overlayControl
        ) {
          return;
        }

        /*
         * Lascia prima al tema la possibilità di gestire il click.
         */
        window.setTimeout(() => {
          const drawer =
            document.querySelector(
              selectors.cartDrawer
            );

          if (isDrawerOpen(drawer)) {
            closeCartDrawerFallback();
          }
        }, 80);
      },
      true
    );

    /*
     * Chiusura di sicurezza anche tramite il tasto Escape.
     */
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') {
          return;
        }

        window.setTimeout(() => {
          const drawer =
            document.querySelector(
              selectors.cartDrawer
            );

          if (isDrawerOpen(drawer)) {
            forceCloseDrawer(drawer);
          }
        }, 120);
      }
    );
  }

  function initialize() {
    captureOriginalDrawerState();
    bindCloseFallbacks();

    if (!correctSectionId) {
      console.warn(
        '[AppHero Cart Fix] Section ID dinamico non trovato al caricamento iniziale.'
      );
    }

    if (!originalDrawerHeader) {
      console.warn(
        '[AppHero Cart Fix] Header originale del drawer non trovato al caricamento iniziale.'
      );
    }

    const observer =
      new MutationObserver(() => {
        scheduleRestoration();
      });

    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true
      }
    );

    scheduleRestoration();
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
})();